import { useEffect } from 'react';
import { useLocation } from '@modern-js/runtime/router';
import type { TrackEvent } from '@hkgai/tracker-sdk';
import { TrackPageNameConfig, PageViewConfig } from '@/config/new.track.config';

type CollectEvent = (events: TrackEvent[]) => void;
type TrackEventParams = Record<string, string | number | boolean | undefined>;

let globalCollectEvent: CollectEvent | null = null;

// 路由到页面名称的映射表
const routeToPageMap: Record<string, TrackPageNameConfig> = {
  '/login': TrackPageNameConfig.LOGIN_PAGE,
  '/notebook': TrackPageNameConfig.FRONT_PAGE,
  '/notebook/meetings': TrackPageNameConfig.MY_MEETINGS_PAGE,
  '/notebook/meetings/my_favorites': TrackPageNameConfig.MY_FAVOURITES_PAGE,
  '/notebook/meetings/recycle_bin': TrackPageNameConfig.RECYCLE_BIN_PAGE,
  '/notebook/create/upload': TrackPageNameConfig.UPLOADING_AUDIO_PAGE,
  '/notebook/create/record': TrackPageNameConfig.RECORDING_AUDIO_PAGE,
  '/notebook/create/transcribe': TrackPageNameConfig.TRANSCRIBE_AUDIO_PAGE,
  '/notebook/cross-summary': TrackPageNameConfig.CROSS_MEETING_SUMMARY_PAGE,
  '/notebook/chat': TrackPageNameConfig.MEETING_ASSISTANT_PAGE,
  '/notebook/chat/history': TrackPageNameConfig.MEETING_ASSISTANT_HISTORY_PAGE,
  '/notebook/chat/*': TrackPageNameConfig.MEETING_ASSISTANT_PAGE,
  '/notebook/hot-words': TrackPageNameConfig.MY_HOT_WORDS_PAGE,
  '/notebook/detail/*': TrackPageNameConfig.MEETING_DETAIL_PAGE,
};

/**
 * 获取当前页面名称 (非组件环境使用)
 * @param pathname 当前路由路径
 */
export const getPageNameByPath = (pathname: string): TrackPageNameConfig => {
  const exactMatch = routeToPageMap[pathname];
  if (exactMatch) {
    return exactMatch;
  }
  for (const route in routeToPageMap) {
    if (route.endsWith('*') && pathname.startsWith(route.slice(0, -1))) {
      return routeToPageMap[route];
    }
  }
  return TrackPageNameConfig.FRONT_PAGE;
};

/**
 * Hook方式获取当前页面名称
 */
export const useCurrentPageName = (): TrackPageNameConfig => {
  const { pathname } = useLocation();
  return getPageNameByPath(pathname);
};

export const setGlobalCollectEvent = (collectEvent: CollectEvent | null) => {
  globalCollectEvent = collectEvent;
};

export const trackEvent = (eventName: string, extraParams?: TrackEventParams) => {
  const { pathname } = window.location;
  const normalizedParams = Object.fromEntries(Object.entries(extraParams || {}).map(([key, value]) => [key, String(value ?? '')]));
  globalCollectEvent?.([
    {
      event: eventName,
      params: {
        pageName: getPageNameByPath(pathname),
        ...normalizedParams,
      },
    },
  ]);
};

/**
 * Hook that fires page_show when the user accesses a page.
 */
export const usePageViewTracker = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    trackEvent(PageViewConfig.PAGE_SHOW);
  }, [pathname]);
};
